import dayjs from 'dayjs';

import { BUCKET_PREFIX } from '../../utils/environment';
import type { GameLabel } from '../game/types';

import type {
  TournamentOfChampions,
  TournamentOfChampionsRound,
  TournamentOfChampionsAssets,
  Leaderboard,
  LeaderboardPrize,
  LeaderboardRanking,
  PayTableMultiplier,
  Tournament,
  TournamentV2Response,
} from './types';
import { TournamentOfChampionsState } from './types';

// ---------------------------------------------------------------------------
//  Constants
// ---------------------------------------------------------------------------

const PLAYER_ID = 2454;
const PLAYER_NAME = 'You';
const TOTAL_ROUNDS = 3;
const LIMIT_TO_ADVANCE = 5;
const ROUND_DURATION_HOURS = 6;
const BETWEEN_ROUNDS_HOURS = 5;

const CHAMPIONS_KEY = `toc-mock-${TOTAL_ROUNDS}rounds`;

const ASSETS_BASE = `${BUCKET_PREFIX}development/assets`;

const DEFAULT_ASSETS: TournamentOfChampionsAssets = {
  bannerBackgroundImg: null,
  bannerAnimation: null,
  betweenRoundsWidgetImg: null,
  shareBackgroundImg: null,
  qualificationLineQualifiedImg: null,
  qualificationLineEliminatedImg: null,
  leaderboardTrophyAnimation: null,
};

// ---------------------------------------------------------------------------
//  Other players — reused across all rounds
// ---------------------------------------------------------------------------

const OTHER_PLAYERS: Omit<LeaderboardRanking, 'position' | 'score'>[] = [
  { playerId: 3001, playerName: 'Alex T.' },
  { playerId: 3002, playerName: 'Jordan R.' },
  { playerId: 3003, playerName: 'Sam K.' },
  { playerId: 3004, playerName: 'Morgan L.' },
  { playerId: 3005, playerName: 'Taylor S.' },
  { playerId: 3006, playerName: 'Casey B.' },
  { playerId: 3007, playerName: 'Riley P.' },
  { playerId: 3008, playerName: 'Drew M.' },
  { playerId: 3009, playerName: 'Quinn H.' },
  { playerId: 3010, playerName: 'Avery D.' },
  { playerId: 3011, playerName: 'Eylon K.' },
  { playerId: 3012, playerName: 'Noah S.' },
  { playerId: 3013, playerName: 'William H.' },
  { playerId: 3014, playerName: 'James G.' },
  { playerId: 3015, playerName: 'Oliver J.' },
  { playerId: 3016, playerName: 'Benjamin F.' },
  { playerId: 3017, playerName: 'Lucas M.' },
  { playerId: 3018, playerName: 'Henry R.' },
  { playerId: 3019, playerName: 'Jack S.' },
];

// ---------------------------------------------------------------------------
//  Helpers
// ---------------------------------------------------------------------------

/** Tournament group key suffix — 1-based (`-r1`, `-r2`, …), aligned with admin + monetization `createFullChampions`. */
function roundKey(roundIndex: number) {
  return `${CHAMPIONS_KEY}-r${roundIndex + 1}`;
}

/**
 * Bracket key the player is segmented into for a given round, in the same shape the backend
 * returns (`{group}-round-{1-based}-bracket-{1-based}`). Used as `playerTournamentKey` on the
 * round payload — the end-of-round claim posts to this key.
 */
function bracketKey(roundIndex: number, bracket = 1): string {
  return `${CHAMPIONS_KEY}-round-${roundIndex + 1}-bracket-${bracket}`;
}

function roundDates(roundIndex: number) {
  const gap = (ROUND_DURATION_HOURS + BETWEEN_ROUNDS_HOURS) * roundIndex;
  const start = dayjs().add(gap, 'hours');
  const end = start.add(ROUND_DURATION_HOURS, 'hours');
  return { start: start.toISOString(), end: end.toISOString() };
}

function pastRoundDates(roundIndex: number, activeRound: number) {
  const hoursBack = (ROUND_DURATION_HOURS + BETWEEN_ROUNDS_HOURS) * (activeRound - roundIndex);
  const start = dayjs().subtract(hoursBack, 'hours');
  const end = start.add(ROUND_DURATION_HOURS, 'hours');
  return { start: start.toISOString(), end: end.toISOString() };
}

function activeRoundDates() {
  const start = dayjs().subtract(1, 'hour');
  const end = start.add(ROUND_DURATION_HOURS, 'hours');
  return { start: start.toISOString(), end: end.toISOString() };
}

const DEFAULT_GAME_IMAGE = `${BUCKET_PREFIX}minigames/widget-images/c3d38f2cd88b4b5e.webp`;

const ALL_FREE_SPINS = [7, 11, 15, 20, 25];
const ALL_COIN_TYPES: ('GC' | 'SC')[] = ['GC', 'SC', 'GC', 'SC', 'GC'];
const ALL_GC_BASE = [2000, 5000, 10000, 25000, 50000];
const ALL_SC_BASE = [50, 150, 500, 1200, 3000];
const ALL_PRIZE_POOL_GC = [500_000, 750_000, 1_000_000, 1_500_000, 2_500_000];
const ALL_PRIZE_POOL_SC = [2_500, 5_000, 7_500, 10_000, 15_000];

function pick<T>(arr: T[], totalRounds: number): T[] {
  if (totalRounds >= arr.length) return arr;
  const step = (arr.length - 1) / (totalRounds - 1);
  return Array.from({ length: totalRounds }, (_, i) => arr[Math.round(i * step)]);
}

const ROUND_FREE_SPINS = pick(ALL_FREE_SPINS, TOTAL_ROUNDS);
const ROUND_COIN_TYPES = pick(ALL_COIN_TYPES, TOTAL_ROUNDS);
const ROUND_GAME_IMAGES = Array.from({ length: TOTAL_ROUNDS }, () => DEFAULT_GAME_IMAGE);
const ROUND_GC_BASE = pick(ALL_GC_BASE, TOTAL_ROUNDS);
const ROUND_SC_BASE = pick(ALL_SC_BASE, TOTAL_ROUNDS);
const ROUND_PRIZE_POOL_GC = pick(ALL_PRIZE_POOL_GC, TOTAL_ROUNDS);
const ROUND_PRIZE_POOL_SC = pick(ALL_PRIZE_POOL_SC, TOTAL_ROUNDS);

function buildPrizeForPosition(position: number, roundIndex: number): LeaderboardPrize {
  const gcBase = ROUND_GC_BASE[roundIndex] ?? 2000;
  const scBase = ROUND_SC_BASE[roundIndex] ?? 50;
  const multiplier = Math.max(1, 4 - position);

  const coinsOnly = position >= LIMIT_TO_ADVANCE;
  const spins = ROUND_FREE_SPINS[roundIndex] ?? 7;
  const coinType = ROUND_COIN_TYPES[roundIndex] ?? 'GC';
  const gameImage = ROUND_GAME_IMAGES[roundIndex] ?? ROUND_GAME_IMAGES[0];

  return {
    gcCoin: gcBase * multiplier,
    scCoin: scBase * multiplier,
    bundleItems: coinsOnly
      ? []
      : [
          {
            bundleType: 'freeSpins' as const,
            bundlePayload: {
              id: `fs-${roundIndex}-${position}`,
              bet: 0,
              type: 'freeSpins',
              rounds: spins,
              endDate: dayjs().add(30, 'days').toISOString(),
              gameIds: [19030 + roundIndex],
              coinType,
              imageUrl: '',
              provider: 'Barouletta Gaming' as const,
              bonusCode: `mockbonus-r${roundIndex}`,
              startDate: dayjs().toISOString(),
              gameImageUrl: gameImage,
            },
            id: `bundle-fs-${roundIndex}-${position}`,
          },
        ],
  };
}

function buildRankings(
  playerPosition: number,
  playerScore: number,
  count = 10,
  roundIndex = 0,
): { rankings: LeaderboardRanking[]; player: LeaderboardRanking } {
  const rankings: LeaderboardRanking[] = [];
  let playerInserted = false;

  for (let i = 0; i < count; i++) {
    const prize = i < LIMIT_TO_ADVANCE ? buildPrizeForPosition(i, roundIndex) : undefined;
    if (i === playerPosition && !playerInserted) {
      rankings.push({
        playerId: PLAYER_ID,
        playerName: PLAYER_NAME,
        position: i,
        score: playerScore,
        prize,
      });
      playerInserted = true;
    } else {
      const otherIdx = playerInserted ? i - 1 : i;
      const other = OTHER_PLAYERS[otherIdx % OTHER_PLAYERS.length];
      rankings.push({
        ...other,
        position: i,
        score: Math.max(0, playerScore - (i + 1) * 50 + 200),
        prize,
      });
    }
  }

  const player = rankings.find((r) => r.playerId === PLAYER_ID) ?? {
    playerId: PLAYER_ID,
    playerName: PLAYER_NAME,
    position: playerPosition,
    score: playerScore,
  };

  return { rankings, player };
}

function buildLeaderboard(
  playerPosition: number,
  playerScore: number,
  gcPool: number,
  scPool: number,
  roundIndex: number,
  leaderboardId: string,
): Leaderboard {
  const { rankings, player } = buildRankings(playerPosition, playerScore, 20, roundIndex);
  return {
    id: leaderboardId,
    rankings,
    player,
    prizePool: gcPool,
    fullPrizePool: { gcCoin: gcPool, scCoin: scPool },
  };
}

function buildLeaderboards(playerPosition: number, playerScore: number, roundIndex: number) {
  const gcPool = (roundIndex + 1) * 10_000;
  const scPool = (roundIndex + 1) * 2;
  // Mock leaderboard IDs mirror the bracket key + coin type so analytics assertions can lock a
  // stable value (real backend uses UUIDs from the leaderboards table).
  const gcId = `${bracketKey(roundIndex)}-gc`;
  const scId = `${bracketKey(roundIndex)}-sc`;
  return {
    gc: buildLeaderboard(playerPosition, playerScore, gcPool, scPool, roundIndex, gcId),
    sc: buildLeaderboard(playerPosition, playerScore, gcPool, scPool, roundIndex, scId),
  };
}

function buildRound(
  roundIndex: number,
  overrides: Partial<TournamentOfChampionsRound> = {},
): TournamentOfChampionsRound {
  const dates = roundDates(roundIndex);
  return {
    roundIndex,
    title: `Round ${roundIndex + 1}`,
    cupActiveImg: null,
    cupInactiveImg: null,
    tournamentGroupKey: roundKey(roundIndex),
    startDate: dates.start,
    endDate: dates.end,
    isFinalized: false,
    playerPosition: null,
    qualified: null,
    playersToAdvanceLimit: roundIndex < TOTAL_ROUNDS - 1 ? LIMIT_TO_ADVANCE : null,
    prizePool: { gcCoin: ROUND_PRIZE_POOL_GC[roundIndex] ?? 500_000, scCoin: ROUND_PRIZE_POOL_SC[roundIndex] ?? 2_500 },
    roundAcknowledged: false,
    playerTournamentKey: bracketKey(roundIndex),
    ...overrides,
  };
}

function buildTournament(
  roundIndex: number,
  overrides: {
    status?: Tournament['status'];
    playerStatus?: Tournament['playerStatus'];
    playerPosition?: number;
    playerScore?: number;
    payTableMultipliers?: PayTableMultiplier[];
  } = {},
): Tournament {
  const {
    status = 'ongoing',
    playerStatus = 'started',
    playerPosition = 2,
    playerScore = 850,
    payTableMultipliers = [],
  } = overrides;
  const dates = activeRoundDates();

  return {
    key: `${roundKey(roundIndex)}-tournament`,
    name: `${CHAMPIONS_KEY} — Round ${roundIndex + 1}`,
    tournamentGroupKey: roundKey(roundIndex),
    tournamentGroup: {
      key: roundKey(roundIndex),
      name: `Round ${roundIndex + 1}`,
      displayName: `Round ${roundIndex + 1}`,
      mode: 'regular',
      type: 'bets',
      maxPositionDisplay: 100,
      startDate: dates.start,
      endDate: dates.end,
      preMessageStartDate: dayjs(dates.start).subtract(1, 'hour').toISOString(),
      postMessageEndDate: dayjs(dates.end).add(1, 'day').toISOString(),
      description: '',
      isActive: true,
      scoreCalculationMethod: 'square_two',
      payTableMultipliers,
      widgetImageUrl: `${ASSETS_BASE}/tournament-defaults/tournament_widget.webp`,
    },
    leaderboards: buildLeaderboards(playerPosition, playerScore, roundIndex),
    teamsLeaderboards: { gc: [], sc: [] },
    prizes: {
      gc: Array.from({ length: LIMIT_TO_ADVANCE }, (_, i) => buildPrizeForPosition(i, roundIndex)),
      sc: Array.from({ length: LIMIT_TO_ADVANCE }, (_, i) => buildPrizeForPosition(i, roundIndex)),
    },
    status,
    playerStatus,
    gameLabel: null,
  };
}

function buildTournamentOfChampions(
  state: TournamentOfChampionsState,
  currentRoundIndex: number,
  rounds: TournamentOfChampionsRound[],
  overrides: Partial<TournamentOfChampions> = {},
): TournamentOfChampions {
  return {
    state,
    championsKey: CHAMPIONS_KEY,
    currentRoundIndex,
    rounds,
    assets: DEFAULT_ASSETS,
    isEliminated: state === TournamentOfChampionsState.BETWEEN_ROUNDS_ELIMINATED,
    ...overrides,
  };
}

function buildResponse(
  tournament: Tournament | null,
  tournamentOfChampions: TournamentOfChampions | null,
  previousTournament: Tournament | null = null,
): TournamentV2Response {
  return {
    tournaments: {
      currentTournament: tournament,
      previousTournament,
      tournamentOfChampions,
    },
  };
}

// ---------------------------------------------------------------------------
//  Scenario builders
// ---------------------------------------------------------------------------

/** Before any round starts — tournament hasn't begun yet */
export function tocPreStart(): TournamentV2Response {
  const firstRoundStart = dayjs().add(24, 'hours');
  const rounds = Array.from({ length: TOTAL_ROUNDS }, (_, i) => {
    const gap = (ROUND_DURATION_HOURS + BETWEEN_ROUNDS_HOURS) * i;
    const start = firstRoundStart.add(gap, 'hours');
    const end = start.add(ROUND_DURATION_HOURS, 'hours');
    return buildRound(i, { startDate: start.toISOString(), endDate: end.toISOString() });
  });
  const toc = buildTournamentOfChampions(TournamentOfChampionsState.PRE_CHAMPIONS, 0, rounds);
  return buildResponse(null, toc);
}

/** Round N is actively running, player is competing */
export function tocActiveRound(
  roundIndex: number,
  playerPosition = 2,
  playerScore = 850,
  payTableMultipliers?: PayTableMultiplier[],
): TournamentV2Response {
  const rounds = Array.from({ length: TOTAL_ROUNDS }, (_, i) => {
    if (i < roundIndex) {
      return buildRound(i, {
        ...pastRoundDates(i, roundIndex),
        isFinalized: true,
        playerPosition: 1,
        qualified: true,
        playerPrize: buildPrizeForPosition(1, i),
      });
    }
    if (i === roundIndex) {
      const dates = activeRoundDates();
      return buildRound(i, {
        startDate: dates.start,
        endDate: dates.end,
        playerPosition,
      });
    }
    return buildRound(i);
  });

  const tournament = buildTournament(roundIndex, { playerPosition, playerScore, payTableMultipliers });
  const toc = buildTournamentOfChampions(TournamentOfChampionsState.ACTIVE_ROUND, roundIndex, rounds);
  return buildResponse(tournament, toc);
}

export function tocClaimLoopBug(): TournamentV2Response {
  const finishedRoundIndex = 0;
  const playerPosition = 0;
  const playerPrize = buildPrizeForPosition(playerPosition, finishedRoundIndex);

  const finishedDates = pastRoundDates(finishedRoundIndex, finishedRoundIndex + 1);
  const nextRoundStart = dayjs().add(1, 'hour');
  const finalRoundStart = dayjs().add(28, 'hours');

  const rounds = [
    buildRound(0, {
      ...finishedDates,
      isFinalized: true,
      playerPosition,
      qualified: true,
      roundAcknowledged: false,
      playerPrize,
    }),
    buildRound(1, {
      startDate: nextRoundStart.toISOString(),
      endDate: nextRoundStart.add(ROUND_DURATION_HOURS, 'hours').toISOString(),
      isFinalized: false,
    }),
    buildRound(2, {
      startDate: finalRoundStart.toISOString(),
      endDate: finalRoundStart.add(ROUND_DURATION_HOURS + 1, 'hours').toISOString(),
      isFinalized: false,
      playersToAdvanceLimit: null,
    }),
  ];

  const previousTournament = buildTournament(finishedRoundIndex, {
    status: 'finished',
    playerStatus: 'started',
    playerPosition,
    playerScore: 0,
  });
  previousTournament.key = bracketKey(finishedRoundIndex);
  previousTournament.tournamentGroup.startDate = finishedDates.start;
  previousTournament.tournamentGroup.endDate = finishedDates.end;
  previousTournament.leaderboards.gc.player.prize = playerPrize;
  previousTournament.leaderboards.sc.player.prize = playerPrize;

  const toc = buildTournamentOfChampions(
    TournamentOfChampionsState.BETWEEN_ROUNDS_QUALIFIED,
    finishedRoundIndex + 1,
    rounds,
  );

  return buildResponse(null, toc, previousTournament);
}

export function tocBetweenRoundsQualified(finishedRoundIndex: number, playerPosition = 1): TournamentV2Response {
  const nextRoundStart = dayjs().add(5, 'hours');
  const rounds = Array.from({ length: TOTAL_ROUNDS }, (_, i) => {
    if (i <= finishedRoundIndex) {
      return buildRound(i, {
        ...pastRoundDates(i, finishedRoundIndex),
        isFinalized: true,
        playerPosition,
        qualified: true,
        playerPrize: buildPrizeForPosition(playerPosition, i),
      });
    }
    if (i === finishedRoundIndex + 1) {
      return buildRound(i, {
        startDate: nextRoundStart.toISOString(),
        endDate: nextRoundStart.add(ROUND_DURATION_HOURS, 'hours').toISOString(),
      });
    }
    return buildRound(i);
  });

  const tournament = buildTournament(finishedRoundIndex, { playerPosition, playerScore: 900 });
  const toc = buildTournamentOfChampions(
    TournamentOfChampionsState.BETWEEN_ROUNDS_QUALIFIED,
    finishedRoundIndex + 1,
    rounds,
  );
  return buildResponse(tournament, toc);
}

export function tocBetweenRoundsEliminated(finishedRoundIndex: number, playerPosition = 5): TournamentV2Response {
  const rounds = Array.from({ length: TOTAL_ROUNDS }, (_, i) => {
    if (i <= finishedRoundIndex) {
      const qualified = i < finishedRoundIndex;
      const pos = qualified ? 1 : playerPosition;
      return buildRound(i, {
        ...pastRoundDates(i, finishedRoundIndex),
        isFinalized: true,
        playerPosition: pos,
        qualified,
        playerPrize: buildPrizeForPosition(pos, i),
      });
    }
    return buildRound(i);
  });

  const toc = buildTournamentOfChampions(
    TournamentOfChampionsState.BETWEEN_ROUNDS_ELIMINATED,
    finishedRoundIndex + 1,
    rounds,
    {
      isEliminated: true,
    },
  );
  return buildResponse(null, toc);
}

const CHAMPIONS_COMPLETED_CAPTURE_KEY = 'toc-test-27051223';

const CAPTURE_PAY_TABLE_MULTIPLIERS: PayTableMultiplier[] = [
  {
    id: '69713b81-cbe5-4e55-8f93-4da4a3fa40ce',
    coinType: null,
    multiplierType: 'CATEGORY',
    targetId: '474',
    targetName: 'slots',
    maxPosition: null,
    multiplierValue: 1,
    isActive: true,
    thumbnailAssets: [
      {
        order: 1,
        imageUrl:
          'https://static.dev.crowncoinscasino.com/development/assets/games/micro-gaming/SMG_bountifulBirdsVF-alternate-all-ftuNy.webp',
      },
      {
        order: 2,
        imageUrl:
          'https://static.dev.crowncoinscasino.com/development/assets/games/pragmatic-play/vs20stckwldsc-thumbnail-Gdrrw.webp',
      },
      {
        order: 3,
        imageUrl:
          'https://static.dev.crowncoinscasino.com/development/assets/games/playtech/gpas_tsgift_pop-thumbnail-ZIzJi.webp',
      },
    ],
  },
  {
    id: '1e9301a4-426a-4d6a-ac68-67f1d6b0b8e9',
    coinType: null,
    multiplierType: 'SLOT',
    targetId: 'rp_537',
    targetName: 'Immortal Ways 88 Charms',
    maxPosition: null,
    multiplierValue: 1,
    isActive: true,
    thumbnailAssets: [
      {
        order: 1,
        imageUrl:
          'https://static.dev.crowncoinscasino.com/development/assets/games/ruby-play/rp_537-thumbnail-yVgGW.webp',
      },
    ],
  },
  {
    id: 'ecaf2b7b-bcfe-4900-80a0-e3a3f9b7e3a0',
    coinType: null,
    multiplierType: 'RANK',
    targetId: null,
    targetName: null,
    maxPosition: 100,
    multiplierValue: 1,
    isActive: true,
    thumbnailAssets: [],
  },
];

const CAPTURE_GAME_LABEL: GameLabel = {
  id: 'aa250656-31c9-491e-ad6f-d1e260e86ed4',
  name: 'Tournament',
  type: 'tournament',
  iconSrc: 'https://static.dev.crowncoinscasino.com/development/assets/game-label/icon-fQzby.png',
  weight: 7,
  isActive: true,
  createdAt: '2024-09-08T08:59:21.145Z',
  updatedAt: '2024-09-08T08:59:21.145Z',
};

const CAPTURE_NULL_ASSETS: TournamentOfChampionsAssets = {
  bannerBackgroundImg: null,
  bannerAnimation: null,
  summaryAnimation: null,
  betweenRoundsWidgetImg: null,
  introAnimation: null,
  endOfRoundAnimation: null,
  betweenRoundsAnimation: null,
  shareBackgroundImg: null,
  qualificationLineQualifiedImg: null,
  qualificationLineEliminatedImg: null,
  leaderboardTrophyAnimation: null,
};
const CAPTURE_FREE_SPINS_GAME_IMAGE = `${ASSETS_BASE}/crown-pop-popup-images/1743347518494-0.webp`;

function buildCapturedPackagePrize(packageId: number, gcCoin: number, scCoin: number): LeaderboardPrize {
  return {
    packageId,
    gcCoin,
    scCoin,
    bundleItems: [
      {
        bundleType: 'freeSpins' as const,
        bundlePayload: {
          id: 'fs-summary-crownpop',
          bet: 0,
          type: 'freeSpins',
          rounds: 1,
          endDate: dayjs().add(30, 'days').toISOString(),
          gameIds: [3009],
          coinType: 'GC',
          imageUrl: '',
          provider: 'Barouletta Gaming' as const,
          bonusCode: 'mockbonus-summary-crownpop',
          startDate: dayjs().toISOString(),
          gameImageUrl: CAPTURE_FREE_SPINS_GAME_IMAGE,
        },
        id: 'bundle-fs-summary-crownpop',
      },
    ],
  };
}

function buildCapturedPackagePrizeWithFreeSpins(packageId: number, gcCoin: number, scCoin: number): LeaderboardPrize {
  return {
    packageId,
    gcCoin,
    scCoin,
    bundleItems: [
      {
        bundleType: 'freeSpins' as const,
        bundlePayload: {
          id: 'fs-summary-crownpop',
          bet: 0,
          type: 'freeSpins',
          rounds: 1,
          endDate: dayjs().add(30, 'days').toISOString(),
          gameIds: [3009],
          coinType: 'GC',
          imageUrl: '',
          provider: 'Barouletta Gaming' as const,
          bonusCode: 'mockbonus-summary-crownpop',
          startDate: dayjs().toISOString(),
          gameImageUrl: CAPTURE_FREE_SPINS_GAME_IMAGE,
        },
        id: 'bundle-fs-summary-crownpop',
      },
    ],
  };
}

function buildChampionsCompletedPreviousTournament(playerPosition: number, prize?: LeaderboardPrize): Tournament {
  const key = CHAMPIONS_COMPLETED_CAPTURE_KEY;
  const player: LeaderboardRanking = {
    playerId: PLAYER_ID,
    playerName: PLAYER_NAME,
    position: playerPosition,
    score: 0,
    ...(prize ? { prize } : {}),
  };
  const slimLeaderboard: Leaderboard = {
    id: '469ecf19-88ce-4245-a5a8-db2716071116',
    rankings: [],
    player,
    prizePool: 0,
    fullPrizePool: { gcCoin: 0, scCoin: 0 },
  };

  return {
    key: `${key}-round-2-bracket-1`,
    name: `${key} - Round 2 - ${key}-round-2-bracket-1`,
    tournamentGroupKey: `${key}-r2`,
    tournamentGroup: {
      key: `${key}-r2`,
      name: `${key}-r2`,
      displayName: '',
      description: '',
      maxPositionDisplay: 100,
      scoreCalculationMethod: 'square_two',
      isActive: true,
      type: 'bets',
      mode: 'regular',
      startDate: '2026-05-27T10:36:00.000Z',
      endDate: '2026-05-27T10:45:00.000Z',
      preMessageStartDate: '2026-05-27T10:20:00.000Z',
      postMessageEndDate: dayjs().add(1, 'day').toISOString(),
      widgetImageUrl: `${ASSETS_BASE}/tournament-defaults/tournament_widget.webp`,
      payTableMultipliers: CAPTURE_PAY_TABLE_MULTIPLIERS,
    },
    leaderboards: { gc: slimLeaderboard, sc: { ...slimLeaderboard, id: undefined } },
    teamsLeaderboards: { gc: [], sc: [] },
    prizes: { gc: [], sc: [] },
    status: 'finished',
    playerStatus: 'started',
    gameLabel: CAPTURE_GAME_LABEL,
  };
}

function buildChampionWonPreviousTournament(): Tournament {
  return buildChampionsCompletedPreviousTournament(0, buildCapturedPackagePrize(529146, 400_000, 20));
}

export function tocChampionWon(): TournamentV2Response {
  const key = CHAMPIONS_COMPLETED_CAPTURE_KEY;
  const lastRoundIndex = 1;

  const rounds: TournamentOfChampionsRound[] = [
    {
      roundIndex: 0,
      title: null,
      cupActiveImg: null,
      cupInactiveImg: null,
      widgetDesignImg: null,
      tournamentGroupKey: `${key}-r1`,
      startDate: '2026-05-27T10:08:00.000Z',
      endDate: '2026-05-27T10:20:00.000Z',
      isFinalized: true,
      playerPosition: 0,
      playerPrize: buildCapturedPackagePrize(529144, 200_000, 10),
      qualified: true,
      playersToAdvanceLimit: 1,
      prizePool: { gcCoin: 300_000, scCoin: 15 },
      roundAcknowledged: true,
      playerTournamentKey: `${key}-round-1-bracket-1`,
    },
    {
      roundIndex: 1,
      title: null,
      cupActiveImg: null,
      cupInactiveImg: null,
      widgetDesignImg: null,
      tournamentGroupKey: `${key}-r2`,
      startDate: '2026-05-27T10:36:00.000Z',
      endDate: '2026-05-27T10:45:00.000Z',
      isFinalized: true,
      playerPosition: 0,
      playerPrize: buildCapturedPackagePrize(529146, 400_000, 20),
      qualified: null,
      playersToAdvanceLimit: null,
      prizePool: { gcCoin: 600_000, scCoin: 30 },
      roundAcknowledged: false,
      playerTournamentKey: `${key}-round-2-bracket-1`,
    },
  ];

  const toc = buildTournamentOfChampions(TournamentOfChampionsState.CHAMPIONS_COMPLETED, lastRoundIndex, rounds, {
    championsKey: key,
    isEliminated: false,
    assets: CAPTURE_NULL_ASSETS,
  });

  return buildResponse(null, toc, buildChampionWonPreviousTournament());
}

/** `CHAMPIONS_COMPLETED` summary where round 1's prize includes a Barouletta free-spins (mini-game). */
export function tocSummaryWithFreeSpins(): TournamentV2Response {
  const response = tocChampionWon();
  const firstRound = response.tournaments.tournamentOfChampions?.rounds[0];
  if (firstRound) {
    firstRound.playerPrize = buildCapturedPackagePrizeWithFreeSpins(529144, 200_000, 10);
  }
  return response;
}

export function tocChampionLostFinal(finalRank = 10): TournamentV2Response {
  const key = CHAMPIONS_COMPLETED_CAPTURE_KEY;
  const lastRoundIndex = 1;
  const finalPosition = Math.max(0, finalRank - 1);

  const rounds: TournamentOfChampionsRound[] = [
    {
      roundIndex: 0,
      title: null,
      cupActiveImg: null,
      cupInactiveImg: null,
      widgetDesignImg: null,
      tournamentGroupKey: `${key}-r1`,
      startDate: '2026-05-27T10:08:00.000Z',
      endDate: '2026-05-27T10:20:00.000Z',
      isFinalized: true,
      playerPosition: 0,
      playerPrize: buildCapturedPackagePrize(529144, 200_000, 10),
      qualified: true,
      playersToAdvanceLimit: 1,
      prizePool: { gcCoin: 300_000, scCoin: 15 },
      roundAcknowledged: true,
      playerTournamentKey: `${key}-round-1-bracket-1`,
    },
    {
      roundIndex: 1,
      title: null,
      cupActiveImg: null,
      cupInactiveImg: null,
      widgetDesignImg: null,
      tournamentGroupKey: `${key}-r2`,
      startDate: '2026-05-27T10:36:00.000Z',
      endDate: '2026-05-27T10:45:00.000Z',
      isFinalized: true,
      playerPosition: finalPosition,
      qualified: null,
      playersToAdvanceLimit: null,
      prizePool: { gcCoin: 600_000, scCoin: 30 },
      roundAcknowledged: false,
      playerTournamentKey: `${key}-round-2-bracket-1`,
    },
  ];

  const toc = buildTournamentOfChampions(TournamentOfChampionsState.CHAMPIONS_COMPLETED, lastRoundIndex, rounds, {
    championsKey: key,
    isEliminated: false,
    assets: CAPTURE_NULL_ASSETS,
  });

  return buildResponse(null, toc, buildChampionsCompletedPreviousTournament(finalPosition));
}

/**
 * Captured backend payload: `BETWEEN_ROUNDS_ELIMINATED` with round 1 finished + unacknowledged
 * (round 0 is finalized, qualified, `roundAcknowledged: false`) so the end-of-round screen shows.
 * Mirrors a real `/v2/tournament/current/{coin}` response captured from the `toc-test-07062046` champions.
 */
const CUSTOM_END_ROUND_KEY = 'toc-test-07062046';

export function tocCustomEndRound(): TournamentV2Response {
  const key = CUSTOM_END_ROUND_KEY;

  const previousTournament: Tournament = {
    key: `${key}-round-1-bracket-1`,
    name: `${key} - Round 1 - Bracket 1`,
    tournamentGroupKey: `${key}-r1`,
    tournamentGroup: {
      key: `${key}-r1`,
      name: `${key} - Round 1`,
      displayName: '',
      description: '',
      maxPositionDisplay: 100,
      scoreCalculationMethod: 'square_two',
      isActive: true,
      type: 'bets',
      mode: 'regular',
      startDate: '2026-06-07T17:48:00.000Z',
      endDate: '2026-06-07T17:50:00.000Z',
      preMessageStartDate: '2026-06-07T16:48:00.000Z',
      postMessageEndDate: '2026-06-08T17:58:00.000Z',
      widgetImageUrl: `${ASSETS_BASE}/tournament-defaults/tournament_widget.webp`,
      payTableMultipliers: [],
    },
    leaderboards: {
      gc: {
        id: '537382d1-1e9d-47ec-8a44-fb6b76bcc810',
        rankings: [],
        player: { playerId: PLAYER_ID, playerName: PLAYER_NAME, position: 1, score: 0 },
        prizePool: 0,
        fullPrizePool: { gcCoin: 0, scCoin: 0 },
      },
      sc: {
        rankings: [],
        player: { playerId: PLAYER_ID, playerName: PLAYER_NAME, position: 1, score: 0 },
        prizePool: 0,
        fullPrizePool: { gcCoin: 0, scCoin: 0 },
      },
    },
    teamsLeaderboards: { gc: [], sc: [] },
    prizes: { gc: [], sc: [] },
    status: 'finished',
    playerStatus: 'started',
    gameLabel: CAPTURE_GAME_LABEL,
  };

  const rounds: TournamentOfChampionsRound[] = [
    {
      roundIndex: 0,
      title: null,
      cupActiveImg: null,
      cupInactiveImg: null,
      widgetDesignImg: null,
      tournamentGroupKey: `${key}-r1`,
      startDate: '2026-06-07T17:48:00.000Z',
      endDate: '2026-06-07T17:50:00.000Z',
      isFinalized: true,
      playerPosition: 1,
      playerPrize: buildCapturedPackagePrizeWithFreeSpins(529150, 20000, 1),
      qualified: true,
      playersToAdvanceLimit: 3,
      prizePool: { gcCoin: 60000, scCoin: 3 },
      roundAcknowledged: false,
      playerTournamentKey: `${key}-round-1-bracket-1`,
    },
    {
      roundIndex: 1,
      title: null,
      cupActiveImg: null,
      cupInactiveImg: null,
      widgetDesignImg: null,
      tournamentGroupKey: `${key}-r2`,
      startDate: '2026-06-07T17:52:00.000Z',
      endDate: '2026-06-07T17:54:00.000Z',
      isFinalized: true,
      playerPosition: null,
      qualified: null,
      playersToAdvanceLimit: 2,
      prizePool: { gcCoin: 40000, scCoin: 2 },
      roundAcknowledged: false,
      playerTournamentKey: null,
    },
    {
      roundIndex: 2,
      title: null,
      cupActiveImg: null,
      cupInactiveImg: null,
      widgetDesignImg: null,
      tournamentGroupKey: `${key}-r3`,
      startDate: '2026-06-07T17:56:00.000Z',
      endDate: '2026-06-07T17:58:00.000Z',
      isFinalized: false,
      playerPosition: null,
      qualified: null,
      playersToAdvanceLimit: null,
      prizePool: { gcCoin: 20000, scCoin: 1 },
      roundAcknowledged: false,
      playerTournamentKey: null,
    },
  ];

  const toc = buildTournamentOfChampions(TournamentOfChampionsState.BETWEEN_ROUNDS_ELIMINATED, 2, rounds, {
    championsKey: key,
    isEliminated: true,
    assets: CAPTURE_NULL_ASSETS,
  });

  return buildResponse(null, toc, previousTournament);
}

/**
 * Captured backend payload: `BETWEEN_ROUNDS_QUALIFIED` on a 3-round champions where each round
 * carries a custom `title` ("avital", "avital 2", "avital 3"). Rounds 1 & 2 are finalized, round 3
 * is the upcoming/active bracket the player advanced into (`currentRoundIndex: 2`).
 * Mirrors a real `/v2/tournament/current/{coin}` response from the `automation-toc-10-06-15-56-41` champions.
 */
const AVITAL_KEY = 'automation-toc-10-06-15-56-41';

export function tocAvitalBetweenRounds(): TournamentV2Response {
  const key = AVITAL_KEY;
  const bracket = `${key}-round-3-bracket-1`;

  const slimLeaderboard: Leaderboard = {
    id: '554312b9-4df1-45dc-af35-a710f4a775b3',
    rankings: [],
    player: { playerId: 26219, playerName: PLAYER_NAME, position: -1, score: 0 },
    prizePool: 0,
    fullPrizePool: { gcCoin: 0, scCoin: 0 },
  };

  const currentTournament: Tournament = {
    key: bracket,
    name: `${key} - Round 3 - Bracket 1`,
    tournamentGroupKey: `${key}-r3`,
    tournamentGroup: {
      key: `${key}-r3`,
      name: `${key} - Round 3`,
      displayName: '',
      mode: 'regular',
      type: 'bets',
      maxPositionDisplay: 100,
      startDate: '2026-06-10T14:31:41.329Z',
      endDate: '2026-06-10T15:01:41.329Z',
      preMessageStartDate: '2026-06-10T14:16:41.329Z',
      postMessageEndDate: '2026-06-12T15:01:41.329Z',
      isActive: true,
      scoreCalculationMethod: 'square_two',
      payTableMultipliers: [],
      widgetImageUrl: `${ASSETS_BASE}/tournament-defaults/tournament_widget.webp`,
    },
    leaderboards: { gc: { ...slimLeaderboard, id: undefined }, sc: slimLeaderboard },
    teamsLeaderboards: { gc: [], sc: [] },
    prizes: { gc: [], sc: [] },
    status: 'upcoming',
    playerStatus: 'pending',
    gameLabel: CAPTURE_GAME_LABEL,
  };

  const rounds: TournamentOfChampionsRound[] = [
    {
      roundIndex: 0,
      title: 'avital',
      cupActiveImg: null,
      cupInactiveImg: null,
      widgetDesignImg: null,
      tournamentGroupKey: `${key}-r1`,
      startDate: '2026-06-10T13:01:41.329Z',
      endDate: '2026-06-10T13:31:41.329Z',
      isFinalized: true,
      playerPosition: null,
      playerPrize: undefined,
      qualified: null,
      playersToAdvanceLimit: 10,
      prizePool: { gcCoin: 0, scCoin: 55 },
      roundAcknowledged: false,
      playerTournamentKey: null,
    },
    {
      roundIndex: 1,
      title: 'avital 2',
      cupActiveImg: null,
      cupInactiveImg: null,
      widgetDesignImg: null,
      tournamentGroupKey: `${key}-r2`,
      startDate: '2026-06-10T13:46:41.329Z',
      endDate: '2026-06-10T14:16:41.329Z',
      isFinalized: true,
      playerPosition: null,
      playerPrize: undefined,
      qualified: null,
      playersToAdvanceLimit: 5,
      prizePool: { gcCoin: 0, scCoin: 15 },
      roundAcknowledged: false,
      playerTournamentKey: null,
    },
    {
      roundIndex: 2,
      title: 'avital 3',
      cupActiveImg: null,
      cupInactiveImg: null,
      widgetDesignImg: null,
      tournamentGroupKey: `${key}-r3`,
      startDate: '2026-06-10T14:31:41.329Z',
      endDate: '2026-06-10T15:01:41.329Z',
      isFinalized: false,
      playerPosition: null,
      playerPrize: undefined,
      qualified: null,
      playersToAdvanceLimit: null,
      prizePool: { gcCoin: 0, scCoin: 1 },
      roundAcknowledged: false,
      playerTournamentKey: bracket,
    },
  ];

  const toc = buildTournamentOfChampions(TournamentOfChampionsState.BETWEEN_ROUNDS_QUALIFIED, 2, rounds, {
    championsKey: key,
    isEliminated: false,
    assets: CAPTURE_NULL_ASSETS,
  });

  return buildResponse(currentTournament, toc);
}

/** Player was eliminated in a specific round (didn't make it to the end) */
export function tocEliminatedAtRound(eliminatedRoundIndex: number, playerPosition = 7): TournamentV2Response {
  const qualifiedPositions = Array.from({ length: TOTAL_ROUNDS }, (_, i) => (i % 2 === 0 ? 1 : 2));
  const rounds = Array.from({ length: TOTAL_ROUNDS }, (_, i) => {
    if (i < eliminatedRoundIndex) {
      const pos = qualifiedPositions[i] ?? 1;
      return buildRound(i, {
        ...pastRoundDates(i, eliminatedRoundIndex),
        isFinalized: true,
        playerPosition: pos,
        qualified: true,
        playerPrize: buildPrizeForPosition(pos, i),
      });
    }
    if (i === eliminatedRoundIndex) {
      return buildRound(i, {
        ...pastRoundDates(i, eliminatedRoundIndex),
        isFinalized: true,
        playerPosition,
        qualified: false,
        playerPrize: buildPrizeForPosition(playerPosition, i),
      });
    }
    return buildRound(i);
  });

  const tournament = buildTournament(eliminatedRoundIndex, {
    status: 'finished',
    playerStatus: 'started',
    playerPosition,
    playerScore: 300,
  });
  const toc = buildTournamentOfChampions(
    TournamentOfChampionsState.BETWEEN_ROUNDS_ELIMINATED,
    eliminatedRoundIndex + 1,
    rounds,
    {
      isEliminated: true,
    },
  );
  return buildResponse(tournament, toc);
}

// ---------------------------------------------------------------------------
//  Prebuilt scenarios — import these directly in the store
// ---------------------------------------------------------------------------

export function defaultActiveRoundPosition(roundIndex: number): number {
  if (roundIndex === 0) return 9;
  return roundIndex % 2 === 0 ? 1 : 3;
}

export function tocActiveRoundScenario(roundIndex: number, playerPosition?: number): TournamentV2Response {
  return tocActiveRound(
    roundIndex,
    playerPosition ?? defaultActiveRoundPosition(roundIndex),
    700 + roundIndex * 250,
    roundIndex === 0 ? CAPTURE_PAY_TABLE_MULTIPLIERS : undefined,
  );
}

/** Active round scenarios for each round (0-indexed) */
const activeRoundScenarios = Object.fromEntries(
  Array.from({ length: TOTAL_ROUNDS }, (_, i) => [`round${i + 1}active`, () => tocActiveRoundScenario(i)]),
);

/** Between-rounds qualified scenarios for each non-final round */
const betweenRoundsScenarios = Object.fromEntries(
  Array.from({ length: TOTAL_ROUNDS - 1 }, (_, i) => [
    `afterRound${i + 1}qualified`,
    () => tocBetweenRoundsQualified(i),
  ]),
);

/** Eliminated scenarios for round 1 and mid-tournament */
const eliminatedScenarios = Object.fromEntries([
  ['eliminatedRound1', () => tocEliminatedAtRound(0)],
  ...(TOTAL_ROUNDS > 2 ? [['eliminatedMidTournament', () => tocEliminatedAtRound(Math.floor(TOTAL_ROUNDS / 2))]] : []),
]);

export const TOC_SCENARIOS: Record<string, () => TournamentV2Response> = {
  preStart: tocPreStart,
  ...activeRoundScenarios,
  ...betweenRoundsScenarios,
  ...eliminatedScenarios,
  championWon: tocChampionWon,
  /** Alias: `CHAMPIONS_COMPLETED` with every round claimed — shows the summary Rive when a TOC mock is active. */
  summary: tocChampionWon,
  /** Summary with a Barouletta free-spins (mini-game) prize on round 1 — see {@link tocSummaryWithFreeSpins}. */
  summaryWithFreeSpins: tocSummaryWithFreeSpins,
  championLostFinal: tocChampionLostFinal,
  /** Reproduces the dev claim-loop bug — see {@link tocClaimLoopBug}. */
  claimLoopBug: tocClaimLoopBug,
  /** Captured `BETWEEN_ROUNDS_ELIMINATED` payload with an unacknowledged end-of-round — see {@link tocCustomEndRound}. */
  customEndRound: tocCustomEndRound,
  /** Captured `BETWEEN_ROUNDS_QUALIFIED` payload with custom round titles ("avital") — see {@link tocAvitalBetweenRounds}. */
  avital: tocAvitalBetweenRounds,
};
